package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.model.*
import com.iptvplayer.shared.domain.repository.DeviceRepository
import com.iptvplayer.shared.domain.repository.RemoteCommandRepository
import com.iptvplayer.shared.presentation.state.RemotePlayUiState
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class RemotePlayViewModel(
    private val remoteCommandRepository: RemoteCommandRepository,
    private val deviceRepository: DeviceRepository
) : ViewModel() {

    private val _state = MutableStateFlow(RemotePlayUiState())
    val state: StateFlow<RemotePlayUiState> = _state.asStateFlow()

    private val _pendingCommand = MutableSharedFlow<RemoteCommand>(replay = 0)
    val pendingCommand: SharedFlow<RemoteCommand> = _pendingCommand.asSharedFlow()

    fun loadOnlineDevices() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            when (val result = deviceRepository.getDevices()) {
                is Result.Success -> {
                    val currentDeviceId = deviceRepository.getLocalDeviceId()
                    val onlineDevices = result.data.filter {
                        it.isOnline && it.id != currentDeviceId
                    }
                    _state.update {
                        it.copy(onlineDevices = onlineDevices, isLoading = false)
                    }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun sendPlayCommand(
        targetDeviceId: String,
        contentId: String,
        contentType: ContentType,
        streamUrl: String,
        contentName: String,
        resumePositionMs: Long? = null
    ) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = remoteCommandRepository.sendCommand(
                targetDeviceId = targetDeviceId,
                action = RemoteAction.PLAY,
                contentId = contentId,
                contentType = contentType,
                streamUrl = streamUrl,
                contentName = contentName,
                resumePositionMs = resumePositionMs
            )) {
                is Result.Success -> {
                    _state.update { it.copy(isLoading = false, commandSent = true) }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun startListeningForCommands() {
        val deviceId = deviceRepository.getLocalDeviceId() ?: return
        viewModelScope.launch {
            remoteCommandRepository.observePendingCommands(deviceId).collect { command ->
                _pendingCommand.emit(command)
                remoteCommandRepository.acknowledgeCommand(command.id, CommandStatus.EXECUTED)
            }
        }
    }

    fun selectDevice(deviceId: String) {
        _state.update { it.copy(selectedDeviceId = deviceId) }
    }

    fun resetCommandSent() {
        _state.update { it.copy(commandSent = false) }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
